import { useState } from "react";
import { ChevronDown, ChevronUp, Lightbulb } from "lucide-react";
import StatusBadge from "./StatusBadge";

function formatEuro(v) {
  if (v == null || isNaN(v)) return "—";
  return `€ ${Number(v).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function HeatmapTable({ items = [] }) {
  const [open, setOpen] = useState({});

  const bgFor = (status) => ({
    EERLIJK: "bg-[rgba(22,163,74,0.04)] hover:bg-[rgba(22,163,74,0.08)]",
    HOOG: "bg-[rgba(234,88,12,0.04)] hover:bg-[rgba(234,88,12,0.08)]",
    OVERDREVEN: "bg-[rgba(220,38,38,0.05)] hover:bg-[rgba(220,38,38,0.09)]",
  })[status] || "";

  const verschilColor = (status) =>
    status === "EERLIJK"
      ? "text-oc-fair"
      : status === "HOOG"
      ? "text-oc-high"
      : "text-oc-excessive";

  return (
    <div data-testid="heatmap-table" className="rounded-oc border border-oc-border overflow-hidden bg-white">
      {/* Desktop header */}
      <div className="hidden md:grid grid-cols-12 px-6 py-4 bg-oc-primary text-white text-[12px] uppercase tracking-[0.08em] font-semibold">
        <div className="col-span-5">Post</div>
        <div className="col-span-2 text-right">Uw prijs</div>
        <div className="col-span-3 text-right">Marktprijs</div>
        <div className="col-span-2 text-right">Status</div>
      </div>
      <div className="divide-y divide-oc-border">
        {items.map((item, idx) => {
          const isOpen = !!open[idx];
          const verschil = item.verschil_procent;
          return (
            <div
              key={idx}
              className={`px-4 sm:px-6 py-4 sm:py-5 transition-colors cursor-pointer ${bgFor(item.status)}`}
              style={{ animation: `fade-in-up 600ms ease-out both ${idx * 80}ms` }}
              onClick={() => setOpen((o) => ({ ...o, [idx]: !o[idx] }))}
              data-testid={`heatmap-row-${idx}`}
            >
              {/* Desktop layout */}
              <div className="hidden md:grid grid-cols-12 items-center gap-2">
                <div className="col-span-5">
                  <p className="font-display font-semibold text-oc-primary text-[16px] leading-snug">
                    {item.omschrijving}
                  </p>
                  <p className={`mt-1 text-[13px] font-mono ${verschilColor(item.status)}`}>
                    {verschil > 0 ? "+" : ""}
                    {Number(verschil || 0).toFixed(1)}%
                  </p>
                </div>
                <div className="col-span-2 text-right font-mono text-oc-primary text-[15px]">
                  {formatEuro(item.geoffreerd)}
                </div>
                <div className="col-span-3 text-right font-mono text-oc-textSecondary text-[14px]">
                  {formatEuro(item.marktprijs_min)} – {formatEuro(item.marktprijs_max)}
                </div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  <StatusBadge status={item.status} />
                  {isOpen ? <ChevronUp className="w-4 h-4 text-oc-textSecondary" /> : <ChevronDown className="w-4 h-4 text-oc-textSecondary" />}
                </div>
              </div>

              {/* Mobile stacked layout */}
              <div className="md:hidden">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-semibold text-oc-primary text-[15px] leading-snug">
                      {item.omschrijving}
                    </p>
                    <p className={`mt-1 text-[12px] font-mono ${verschilColor(item.status)}`}>
                      {verschil > 0 ? "+" : ""}
                      {Number(verschil || 0).toFixed(1)}%
                    </p>
                  </div>
                  <StatusBadge status={item.status} compact />
                </div>
                <div className="mt-3 flex items-center justify-between text-[13px]">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-oc-textSecondary font-semibold">Uw prijs</p>
                    <p className="font-mono text-oc-primary">{formatEuro(item.geoffreerd)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wide text-oc-textSecondary font-semibold">Marktprijs</p>
                    <p className="font-mono text-oc-textSecondary text-[12px]">
                      {formatEuro(item.marktprijs_min)} – {formatEuro(item.marktprijs_max)}
                    </p>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-oc-textSecondary" /> : <ChevronDown className="w-4 h-4 text-oc-textSecondary" />}
                </div>
              </div>

              {isOpen && (
                <div className="mt-4 pt-4 border-t border-oc-border grid md:grid-cols-2 gap-4 text-[14px]">
                  <div>
                    <h5 className="font-semibold text-oc-primary mb-1 text-[12px] uppercase tracking-wide">
                      Uitleg
                    </h5>
                    <p className="text-oc-textSecondary leading-relaxed">{item.uitleg}</p>
                  </div>
                  {item.tip && (
                    <div className="rounded-md bg-oc-subtle border border-oc-border p-4 flex gap-3">
                      <Lightbulb className="w-4 h-4 text-oc-secondary shrink-0 mt-0.5" />
                      <div>
                        <h5 className="font-semibold text-oc-primary text-[12px] uppercase tracking-wide mb-1">
                          Tip
                        </h5>
                        <p className="text-oc-textSecondary">{item.tip}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
